/**
 * 日期与天数工具（PRD §4.3、§6.2）。
 *
 * 所有显示时间使用 Asia/Shanghai（北京时间）；自然日计算以北京时间当天 00:00
 * 为基准，起始日计入。
 */

/** 相识日常量（PRD §4.3） */
export const PAIR_START_DATE = '2016-01-01';

/** 时间精度 */
export type DatePrecision = 'year' | 'month' | 'day';

/** 用户输入的日期解析结果 */
export interface ParsedUserDate {
  /** 标准化为 UTC ISO 字符串（保持原始精度对应的最小时间） */
  iso: string;
  /** 精度 */
  precision: DatePrecision;
  /** 可读的 Y/M/D 数字 */
  year: number;
  month: number;
  day: number;
}

/** 内部：上海时区 parts */
interface ShanghaiParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const SHANGHAI_TZ = 'Asia/Shanghai';

const dateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: SHANGHAI_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/** 把 Date 或 ISO 字符串拆成上海时区的年/月/日/时/分/秒。 */
export function getShanghaiParts(input: string | Date): ShanghaiParts {
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) {
    throw new Error(`format.getShanghaiParts: invalid date input ${String(input)}`);
  }
  const parts = dateTimeFormatter.formatToParts(d);
  const lookup: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== 'literal') {
      lookup[part.type] = part.value;
    }
  }
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
    hour: Number(lookup.hour === '24' ? '00' : lookup.hour),
    minute: Number(lookup.minute),
    second: Number(lookup.second),
  };
}

/** 构造上海时区当日 00:00 的 UTC Date。 */
function shanghaiStartOfDay(year: number, month: number, day: number): Date {
  // 用 "Asia/Shanghai 凌晨"的近似表示：先把 Y/M/D 视为 UTC，再通过补足偏移
  // 反推出正确的 UTC 瞬间；以下方法在所有时区都正确（不依赖运行环境时区）。
  const utcOfMidnight = Date.UTC(year, month - 1, day, 0, 0, 0);
  const guess = new Date(utcOfMidnight);
  const parts = getShanghaiParts(guess);
  // 计算上海时区偏移（小时）
  const offsetHours =
    Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) -
    utcOfMidnight;
  return new Date(utcOfMidnight - offsetHours);
}

/**
 * 计算两个日期之间的自然日差（含起日，PRD §4.3）。
 *
 * - 使用 Asia/Shanghai 当日 00:00 作为日期基准；
 * - 时区无关，结果与运行环境无关；
 * - `daysBetween(PAIR_START_DATE, PAIR_START_DATE) === 1`。
 */
export function daysBetween(startISO: string, nowISO: string | Date): number {
  const startParts = getShanghaiParts(startISO);
  const nowParts = getShanghaiParts(nowISO);
  const start = shanghaiStartOfDay(startParts.year, startParts.month, startParts.day);
  const end = shanghaiStartOfDay(nowParts.year, nowParts.month, nowParts.day);
  const diffMs = end.getTime() - start.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
}

/** 友好的日期显示（绝对时间 → 上海时区）。 */
export function formatInShanghai(input: string | Date, opts?: Intl.DateTimeFormatOptions): string {
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const fmt = new Intl.DateTimeFormat('zh-CN', {
    timeZone: SHANGHAI_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...opts,
  });
  return fmt.format(d);
}

/** 显示 YYYY 年 M 月 D 日（中文）。 */
export function formatPrettyDate(input: string | Date): string {
  return formatInShanghai(input, {
    era: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** 将业务日期（YYYY-MM-DD）显示为中文日历格式；非完整日期原样返回。 */
export function formatDateChinese(input: string | null | undefined): string {
  const value = (input ?? '').trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  return `${match[1]}年${Number(match[2])}月${Number(match[3])}日`;
}

/**
 * 解析用户输入的日期字符串，支持三种精度：
 * - `YYYY`        → 精度 'year'；
 * - `YYYY-MM`     → 精度 'month'；
 * - `YYYY-MM-DD`  → 精度 'day'。
 *
 * 返回的 `iso` 是该精度对应的最早一刻（上海时区当天 00:00 转为 UTC ISO）。
 */
export function parseUserDate(input: string): ParsedUserDate {
  const trimmed = input.trim();
  if (!/^\d{4}(-\d{2}(-\d{2})?)?$/.test(trimmed)) {
    throw new Error(`format.parseUserDate: invalid input ${JSON.stringify(input)}`);
  }
  const [y, m, d] = trimmed.split('-').map((v) => Number(v));
  const year = y;
  const month = m ?? 1;
  const day = d ?? 1;
  if (month < 1 || month > 12) {
    throw new Error(`format.parseUserDate: invalid month ${m}`);
  }
  // 校验 day 范围（用 UTC Date 不会受本地时区干扰）
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    throw new Error(`format.parseUserDate: invalid day ${d} for ${year}-${month}`);
  }
  const precision: DatePrecision = trimmed.length === 4
    ? 'year'
    : trimmed.length === 7
      ? 'month'
      : 'day';
  const startOfDayShanghai = shanghaiStartOfDay(year, month, day);
  return {
    iso: startOfDayShanghai.toISOString(),
    precision,
    year,
    month,
    day,
  };
}

/** 精度 → 中文显示标签，例如 2018 年 6 月。 */
export function formatPrecisionDate(parsed: ParsedUserDate): string {
  if (parsed.precision === 'year') {
    return `${parsed.year} 年`;
  }
  if (parsed.precision === 'month') {
    return `${parsed.year} 年 ${parsed.month} 月`;
  }
  return `${parsed.year} 年 ${parsed.month} 月 ${parsed.day} 日`;
}

/** 当前时间（ISO 字符串） */
export function nowIso(): string {
  return new Date().toISOString();
}

/** 回收站 7 天倒计时常量（毫秒） */
export const RECYCLE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** 删除申请 3 天倒计时常量（毫秒） */
export const DELETE_REQUEST_TTL_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * 格式化回收站剩余时间（PRD §9.3：7 天后永久删除）。
 *
 * - 输入为 ISO 字符串形式的 `expiresAt`；
 * - 已过期返回 `'已过期'`；
 * - 不到 1 小时显示 `不足1小时`；
 * - 剩余大于 0 时返回 `D天H小时` 字符串。
 */
export function formatRecycleCountdown(deadlineISO: string, nowISO?: string): string {
  const now = nowISO ? new Date(nowISO).getTime() : Date.now();
  const deadline = new Date(deadlineISO).getTime();
  if (Number.isNaN(deadline)) return '已过期';
  const remainingMs = deadline - now;
  if (remainingMs <= 0) return '已过期';
  if (remainingMs < 60 * 60 * 1000) return '不足1小时';
  const days = Math.floor(remainingMs / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  return `${days}天${hours}小时`;
}

/**
 * 格式化删除申请剩余时间（PRD §9.2：3 天未处理自动拒绝）。
 */
export function formatDeleteRequestCountdown(deadlineISO: string, nowISO?: string): string {
  const now = nowISO ? new Date(nowISO).getTime() : Date.now();
  const deadline = new Date(deadlineISO).getTime();
  if (Number.isNaN(deadline)) return '已过期';
  const remainingMs = deadline - now;
  if (remainingMs <= 0) return '已到期';
  const totalHours = Math.ceil(remainingMs / (60 * 60 * 1000));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days <= 0) return `${hours}小时后自动拒绝`;
  return `${days}天${hours}小时后自动拒绝`;
}

/** 计算删除申请的自动拒绝截止时间（now + 72h）。 */
export function deleteRequestDeadline(nowISO?: string): string {
  const now = nowISO ? new Date(nowISO).getTime() : Date.now();
  return new Date(now + DELETE_REQUEST_TTL_MS).toISOString();
}

/** 计算回收项的永久删除时间（recycledAt + 7 天）。 */
export function recycleDeadline(recycledAtISO: string): string {
  const t = new Date(recycledAtISO).getTime();
  if (Number.isNaN(t)) throw new Error(`format.recycleDeadline: invalid input ${recycledAtISO}`);
  return new Date(t + RECYCLE_TTL_MS).toISOString();
}
